<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const FILTERS = ['all', 'conversations', 'notes'];

const STATUS_ICON_STYLES = {
  open: 'bg-n-blue-3 text-n-blue-11',
  pending: 'bg-n-amber-3 text-n-amber-11',
  resolved: 'bg-n-teal-3 text-n-teal-11',
  snoozed: 'bg-n-slate-3 text-n-slate-11',
};

const activeFilter = ref('all');

const conversationsByContactId = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const notesByContactId = useMapGetter('contactNotes/getAllNotesByContactId');
const stateInbox = useMapGetter('inboxes/getInboxById');
const conversationUiFlags = useMapGetter('contactConversations/getUIFlags');
const notesUiFlags = useMapGetter('contactNotes/getUIFlags');

const isFetching = computed(
  () => conversationUiFlags.value.isFetching || notesUiFlags.value.isFetching
);

const { getPlainText } = useMessageFormatter();

// Notes and messages are markdown; render previews as plain text.
const stripMarkup = content =>
  content ? getPlainText(content).replace(/\s+/g, ' ').trim() : '';

const conversationItems = computed(() =>
  conversationsByContactId.value(route.params.contactId).map(conversation => {
    const { customAttributes = {}, lastNonActivityMessage } = conversation;
    const preview =
      customAttributes?.subject ||
      stripMarkup(lastNonActivityMessage?.content) ||
      t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.CONVERSATION_FALLBACK', {
        id: conversation.displayId ?? conversation.id,
      });

    return {
      key: `conversation-${conversation.id}`,
      kind: 'conversations',
      timestamp: conversation.timestamp || conversation.createdAt || 0,
      preview,
      conversationId: conversation.id,
      status: conversation.status,
      inboxName: stateInbox.value(conversation.inboxId)?.name || '',
    };
  })
);

const noteItems = computed(() =>
  notesByContactId.value(route.params.contactId).map(note => ({
    key: `note-${note.id}`,
    kind: 'notes',
    timestamp: note.createdAt || 0,
    preview: stripMarkup(note.content),
    author: note.user?.name || 'Bot',
  }))
);

const stageLabel = stage =>
  t(`CONTACTS_LAYOUT.DETAILS.LIFECYCLE.${stage.toUpperCase()}`);

const lifecycleItems = computed(() =>
  (props.selectedContact?.additionalAttributes?.lifecycleEvents || [])
    .filter(event => event?.from && event?.to)
    .map((event, index) => ({
      key: `lifecycle-${index}-${event.at}`,
      kind: 'system',
      timestamp: event.at || 0,
      preview: t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.LIFECYCLE_EVENT', {
        from: stageLabel(event.from),
        to: stageLabel(event.to),
      }),
      author: event.actor,
    }))
);

const timelineItems = computed(() => {
  const items = [
    ...conversationItems.value,
    ...noteItems.value,
    ...lifecycleItems.value,
  ].sort((a, b) => b.timestamp - a.timestamp);
  if (activeFilter.value === 'all') return items;
  return items.filter(({ kind }) => kind === activeFilter.value);
});

const filterLabel = filter =>
  t(`CONTACTS_LAYOUT.DETAILS.ACTIVITY.FILTERS.${filter.toUpperCase()}`);

const statusLabel = status =>
  t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`);

const timeLabel = timestamp =>
  timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';

const openConversation = conversationId => {
  router.push({
    path: frontendURL(
      conversationUrl({
        accountId: route.params.accountId,
        id: conversationId,
      })
    ),
  });
};
</script>

<template>
  <CardLayout>
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.TITLE') }}
      </h4>
      <div class="flex items-center p-0.5 rounded-lg bg-n-alpha-2">
        <button
          v-for="filter in FILTERS"
          :key="filter"
          type="button"
          class="px-3 text-sm font-medium transition-colors rounded-md h-7"
          :class="
            filter === activeFilter
              ? 'bg-n-solid-1 shadow-sm text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeFilter = filter"
        >
          {{ filterLabel(filter) }}
        </button>
      </div>
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="timelineItems.length"
      class="flex flex-col divide-y divide-n-weak"
    >
      <template v-for="item in timelineItems" :key="item.key">
        <button
          v-if="item.kind === 'conversations'"
          type="button"
          class="flex items-start w-full gap-3 px-2 py-3 -mx-2 transition-colors rounded-lg cursor-pointer text-start hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
          @click="openConversation(item.conversationId)"
        >
          <span
            class="grid rounded-lg shrink-0 size-8 place-items-center"
            :class="
              STATUS_ICON_STYLES[item.status] || STATUS_ICON_STYLES.snoozed
            "
          >
            <span class="i-lucide-message-circle size-4" />
          </span>
          <span class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="text-sm truncate text-n-slate-12">
              {{ item.preview }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{
                [item.inboxName, statusLabel(item.status)]
                  .filter(Boolean)
                  .join(' · ')
              }}
            </span>
          </span>
          <span class="text-xs shrink-0 text-n-slate-10 pt-0.5">
            {{ timeLabel(item.timestamp) }}
          </span>
        </button>
        <div
          v-else-if="item.kind === 'notes'"
          class="flex items-start w-full gap-3 py-3"
        >
          <span
            class="grid rounded-lg shrink-0 size-8 place-items-center bg-n-amber-2 text-n-amber-11"
          >
            <span class="i-lucide-sticky-note size-4" />
          </span>
          <span class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="text-sm truncate text-n-slate-12">
              {{ item.preview }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{
                `${t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.NOTE_LABEL')} · ${item.author}`
              }}
            </span>
          </span>
          <span class="text-xs shrink-0 text-n-slate-10 pt-0.5">
            {{ timeLabel(item.timestamp) }}
          </span>
        </div>
        <div v-else class="flex items-start w-full gap-3 py-3">
          <span
            class="grid rounded-lg shrink-0 size-8 place-items-center bg-n-iris-3 text-n-iris-11"
          >
            <span class="i-lucide-trending-up size-4" />
          </span>
          <span class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="text-sm truncate text-n-slate-12">
              {{ item.preview }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{
                item.author
                  ? t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.LIFECYCLE_ACTOR', {
                      actor: item.author,
                    })
                  : t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.LIFECYCLE_AUTO')
              }}
            </span>
          </span>
          <span class="text-xs shrink-0 text-n-slate-10 pt-0.5">
            {{ timeLabel(item.timestamp) }}
          </span>
        </div>
      </template>
    </div>
    <p v-else class="py-8 text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.DETAILS.ACTIVITY.EMPTY_STATE') }}
    </p>
  </CardLayout>
</template>
